import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { Bandeau, Carte, Squelette } from '../components/ui';
import { montant, montantCourt } from '../lib/format';
import type { LigneCapacite } from '../api/types';

interface Reponse { lignes: LigneCapacite[]; alertes: { niveau: string; texte: string }[]; note: string }

/**
 * L'ecran qui met leur idee en image.
 * Ce qui est encaisse n'est pas ce qui est gagne ce mois-ci : quatre chiffres,
 * jamais melanges.
 */
export default function Capacite() {
  const [d, setD] = useState<Reponse | null>(null);
  useEffect(() => { api.get<Reponse>('/analytique/capacite?mois=12').then(setD); }, []);
  if (!d) return <Squelette lignes={8} />;

  const max = Math.max(...d.lignes.map((l) => l.potentielMaxMinor), 1);

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Trésorerie &amp; prévision</h1>
          <p>Ce que vous avez encaissé n'est pas ce que vous avez gagné ce mois-ci. Cet écran separe les deux.</p>
        </div>
      </div>

      <Carte titre="12 mois glissants">
        <div className="barres">
          {d.lignes.map((l) => {
            const hRest = (l.restantAVendreMinor / max) * 100;
            const hRes = ((l.reserveMinor - l.dejaEncaisseMinor) / max) * 100;
            const hEnc = (l.dejaEncaisseMinor / max) * 100;
            return (
              <div className="col" key={l.mois} title={`${l.libelle} — potentiel ${montant(l.potentielMaxMinor)}`}>
                <div className="pile">
                  <div className="rest" style={{ height: `${hRest}%` }} />
                  <div className="res" style={{ height: `${hRes}%` }} />
                  <div className="enc" style={{ height: `${hEnc}%` }} />
                </div>
                <div className="mini sourdine" style={{ whiteSpace: 'nowrap', fontSize: 11 }}>{l.libelle.split(' ')[0]!.slice(0, 4)}</div>
              </div>
            );
          })}
        </div>
        <div className="legende">
          <span><i style={{ background: 'var(--succes-doux)', border: '1px solid var(--succes-bord)' }} />Restant à vendre</span>
          <span><i style={{ background: 'var(--primaire)' }} />Réservé, pas encore encaissé</span>
          <span><i style={{ background: 'var(--primaire-fonce)' }} />Réservé ET déjà encaissé</span>
        </div>
      </Carte>

      <div style={{ marginTop: 16 }}>
        <Carte titre="Mois par mois" pad={false}>
          <div className="enveloppe-tbl">
            <table className="tbl compact">
              <thead>
                <tr>
                  <th>Mois</th><th className="droite">Potentiel max</th><th className="droite">Réservé</th>
                  <th className="droite">dont déjà encaissé</th><th className="droite">Restant à vendre</th>
                  <th className="droite">Encaissements réels</th><th className="droite">CA constaté</th><th>Remplissage</th>
                </tr>
              </thead>
              <tbody>
                {d.lignes.map((l) => (
                  <tr key={l.mois}>
                    <td><b>{l.libelle}</b></td>
                    <td className="droite montant sourdine">{montantCourt(l.potentielMaxMinor)}</td>
                    <td className="droite montant">{montantCourt(l.reserveMinor)}</td>
                    <td className="droite montant" style={{ color: l.dejaEncaisseMinor > 0 ? 'var(--attention)' : undefined }}>
                      {montantCourt(l.dejaEncaisseMinor)}
                    </td>
                    <td className="droite montant" style={{ color: 'var(--succes)' }}>{montantCourt(l.restantAVendreMinor)}</td>
                    <td className="droite montant">{montantCourt(l.encaissementsReelsMinor)}</td>
                    <td className="droite montant sourdine">{montantCourt(l.caConstateMinor)}</td>
                    <td>
                      <div style={{ background: 'var(--zebre)', borderRadius: 4, height: 8, width: 78, overflow: 'hidden' }}>
                        <div style={{ background: 'var(--primaire)', height: '100%', width: `${Math.min(100, l.tauxRemplissage)}%` }} />
                      </div>
                      <span className="mini sourdine">{l.tauxRemplissage} %</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Carte>
      </div>

      {d.alertes.length > 0 && (
        <div className="grille g2" style={{ marginTop: 16 }}>
          {d.alertes.map((a, i) => (
            <Bandeau key={i} type={a.niveau === 'attention' ? 'warn' : 'info'}>{a.texte}</Bandeau>
          ))}
        </div>
      )}

      <p className="mini sourdine" style={{ marginTop: 16, maxWidth: 760 }}>{d.note}</p>
    </>
  );
}
